import React, { useEffect, useState } from 'react';
import { Mail, Send, Users2 } from 'lucide-react';
import { FaLinkedin, FaYoutube, FaReddit } from 'react-icons/fa';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import api from '../api/api';

const COMMUNITY_LINKS = [
    {
        name: 'LinkedIn',
        href: 'https://www.linkedin.com/company/buildmybizbrand/',
        icon: FaLinkedin,
        colorClasses: { light: 'bg-blue-100 text-blue-600', dark: 'bg-blue-900/50 text-blue-400' },
    },
    {
        name: 'YouTube',
        href: 'https://www.youtube.com/@BuildMyBizBrand',
        icon: FaYoutube,
        colorClasses: { light: 'bg-red-100 text-red-600', dark: 'bg-red-900/50 text-red-400' },
    },
    {
        name: 'Reddit',
        href: 'https://www.reddit.com/r/BuildMyBizBrand/',
        icon: FaReddit,
        colorClasses: { light: 'bg-orange-100 text-orange-600', dark: 'bg-orange-900/50 text-orange-400' },
    },
];

export default function Contact() {
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Auto-fill the name from the logged-in user's profile, without
    // clobbering anything they've already typed.
    useEffect(() => {
        if (!user) return;
        const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
        if (!fullName) return;
        setFormData((prev) => (prev.name ? prev : { ...prev, name: fullName }));
    }, [user]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            await api.post('/contact', formData);
            setSubmitted(true);
            setTimeout(() => setSubmitted(false), 5000); // Reset UI after 5 seconds
            setFormData((prev) => ({ ...prev, email: '', message: '' }));
        } catch (err) {
            setError(err.response?.data?.error || 'Could not send your message. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    return (
        <div className={`min-h-screen pt-24 pb-12 transition-colors duration-500 ${isDarkMode ? 'bg-surface text-white' : 'bg-slate-50 text-slate-900'}`}>
            <div className="container mx-auto px-4 max-w-5xl">

                <div className="text-center mb-12">
                    <h1 className={`text-4xl font-bold mb-4 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Get in Touch</h1>
                    <p className={`max-w-2xl mx-auto ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Have questions about MyBizBrand or interested in becoming a coach?
                        Send us a message and our team will get back to you shortly.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    {/* Contact Info Sidebar */}
                    <div className="md:col-span-1 space-y-6">
                        <div className={`p-6 rounded-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-100 shadow-sm'}`}>
                            <div className="flex items-center gap-4 mb-4">
                                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                                    <Mail className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className={`font-bold text-sm ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Email Us</h3>
                                    <a
                                        href="mailto:coaching@buildmybizbrand.com"
                                        className={`text-sm hover:underline break-all ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}
                                    >
                                        coaching@buildmybizbrand.com
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className={`p-6 rounded-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-100 shadow-sm'}`}>
                            <div className="flex items-center gap-4 mb-4">
                                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-primary-900/50 text-primary-400' : 'bg-primary-100 text-primary-600'}`}>
                                    <Users2 className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className={`font-bold text-sm ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Join Our Community</h3>
                                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Follow along and connect with us</p>
                                </div>
                            </div>
                            <div className="space-y-2">
                                {COMMUNITY_LINKS.map(({ name, href, icon: Icon, colorClasses }) => (
                                    <a
                                        key={name}
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex items-center gap-3 p-2.5 rounded-xl transition-colors ${
                                            isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className={`p-2 rounded-lg ${isDarkMode ? colorClasses.dark : colorClasses.light}`}>
                                            <Icon className="w-4 h-4" />
                                        </div>
                                        <span className={`text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>{name}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className={`md:col-span-2 p-8 rounded-3xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 shadow-primary-900/20' : 'bg-white border-slate-100 shadow-xl shadow-primary-200/20'}`}>
                        {submitted ? (
                            <div className="h-full flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-300">
                                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isDarkMode ? 'bg-primary-900/50 text-primary-400' : 'bg-primary-100 text-primary-600'}`}>
                                    <Send className="w-8 h-8" />
                                </div>
                                <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`}>Message Sent!</h2>
                                <p className={`mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Thank you for reaching out. We'll be in touch soon.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label className={`text-sm font-semibold mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>Your Name</label>
                                        <input
                                            type="text"
                                            name="name"
                                            required
                                            value={formData.name}
                                            onChange={handleChange}
                                            className={`w-full p-3 rounded-xl border bg-transparent focus:ring-4 focus:ring-blue-400/20 outline-none transition-all ${isDarkMode ? 'border-slate-700 focus:border-blue-500 text-slate-100 placeholder-slate-500' : 'border-slate-200 focus:border-blue-600 text-slate-900 placeholder-slate-400'}`}
                                            placeholder="John Doe"
                                        />
                                    </div>
                                    <div>
                                        <label className={`text-sm font-semibold mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>Email Address</label>
                                        <input
                                            type="email"
                                            name="email"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            className={`w-full p-3 rounded-xl border bg-transparent focus:ring-4 focus:ring-blue-400/20 outline-none transition-all ${isDarkMode ? 'border-slate-700 focus:border-blue-500 text-slate-100 placeholder-slate-500' : 'border-slate-200 focus:border-blue-600 text-slate-900 placeholder-slate-400'}`}
                                            placeholder="john@example.com"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className={`text-sm font-semibold mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>Message</label>
                                    <textarea
                                        name="message"
                                        required
                                        value={formData.message}
                                        onChange={handleChange}
                                        rows="5"
                                        className={`w-full p-3 rounded-xl border bg-transparent focus:ring-4 focus:ring-blue-400/20 outline-none transition-all ${isDarkMode ? 'border-slate-700 focus:border-blue-500 text-slate-100 placeholder-slate-500' : 'border-slate-200 focus:border-blue-600 text-slate-900 placeholder-slate-400'}`}
                                        placeholder="Tell us how we can help..."
                                    />
                                </div>
                                {error && <p className="text-red-500 text-sm font-medium">⚠️ {error}</p>}
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Send className="w-5 h-5" />
                                    {submitting ? 'Sending...' : 'Send Message'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
